<script setup>
// ===== IMPORTS =====
import { ref, computed, watch, onDeactivated } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import {
  helpers,
  required,
  requiredIf,
  maxLength,
} from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import TemplateButtonsEditor from './TemplateButtonsEditor.vue';
import CarouselCardEditor from './CarouselCardEditor.vue';
import WhatsappTemplatePreview from './WhatsappTemplatePreview.vue';
import FileUpload from 'vue-upload-component';
import CollapsibleSection from 'dashboard/components/ui/CollapsibleSection.vue';
import {
  WHATSAPP_CATEGORY_OPTIONS,
  LANGUAGE_OPTIONS,
  HEADER_OPTIONS,
  MEDIA_CONFIG,
  VALIDATION_CONSTANTS,
  AUTH_TEMPLATE_STRINGS,
} from '../constants.js';
import {
  allKeysRequired,
  isValidHeaderFile,
  extractVariables,
  sequentialVariables,
  templateNameValidator,
  onlyOneVariable,
  noAdjacentVariables,
  noStartEndVariable,
  validateCarouselCards,
} from '../validations.js';
import { whatsappTemplatePayload } from 'dashboard/helper/whatsappTemplatePayload.js';

const defaultTemplate = {
  name: '',
  category: null,
  language: null,
  header: 'NONE',
  headerText: '',
  headerMediaFile: null,
  headerMediaUrl: '',
  headerMediaHandle: '',
  body: '',
  footer: '',
  buttons: [],
  format: 'STANDARD',
  cards: [],
  // Authentication template specific fields
  authCodeDeliveryMethod: 'COPY_CODE', // 'ZERO_TAP', 'ONE_TAP', 'COPY_CODE'
  addSecurityRecommendation: false,
  addExpirationTime: false,
  expirationTimeMinutes: null,
  packageName: '',
  signatureHash: '',
};
const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const isCreating = ref(false);
// ===== REFS =====
const template = ref({ ...defaultTemplate });
const templateButtonsEditor = ref(null);
const carouselCardEditor = ref(null);

const sampleValues = ref({
  bodyValues: {},
  headerValues: {},
  buttonsValues: {},
});

// Category-specific computed properties
const isAuthTemplate = computed(
  () => template.value.category === 'AUTHENTICATION'
);

// Computed authentication text
const authText = computed(() => {
  // text
  const computedAuthText = template.value.language
    ? AUTH_TEMPLATE_STRINGS[template.value.language]
    : AUTH_TEMPLATE_STRINGS.en_US;

  // body
  let authBodyText = computedAuthText.BASE_CONTENT;
  if (template.value.addSecurityRecommendation) {
    authBodyText += ` ${computedAuthText.SECURITY_RECOMMENDATION}`;
  }

  // footer
  let authFooterText = '';
  if (
    template.value.addExpirationTime &&
    template.value.expirationTimeMinutes
  ) {
    authFooterText = computedAuthText.EXPIRATION_TEXT.replace(
      '{minutes}',
      template.value.expirationTimeMinutes
    );
  }

  return {
    body: authBodyText,
    footer: authFooterText,
  };
});

// Computed
const headerVariables = computed(() => {
  if (template.value.header !== 'TEXT') return [];
  return extractVariables(template.value.headerText);
});

const previewHeader = computed(() => {
  if (template.value.header === 'TEXT') {
    let text = template.value.headerText || '';
    headerVariables.value.forEach(varNum => {
      const userValue = sampleValues.value.headerValues[varNum];
      if (userValue && userValue.trim() !== '') {
        text = text.replace(`{{${varNum}}}`, userValue);
      }
    });
    return text;
  }
  return null;
});

const bodyVariables = computed(() => {
  return extractVariables(template.value.body).sort((a, b) => a - b);
});

const escapeHtml = text => {
  if (!text) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

const previewBodyWithHighlight = computed(() => {
  let text = template.value.body || 'Your message body will appear here...';

  if (!template.value.body) return text;

  // Step 1: Replace variables with styled spans
  bodyVariables.value.forEach(varNum => {
    const userValue = sampleValues.value.bodyValues[varNum];
    const hasValue = userValue && userValue.trim();
    const displayValue = hasValue ? userValue : `{{${varNum}}}`;

    // Escape HTML to prevent XSS
    const escapedValue = escapeHtml(displayValue);

    const replacement = hasValue
      ? `<span class="bg-green-700 text-white px-1 py-[1px] rounded">${escapedValue}</span>`
      : `<span>{{${varNum}}}</span>`;

    text = text.replace(new RegExp(`\\{\\{${varNum}\\}\\}`, 'g'), replacement);
  });

  // Step 2: Convert ALL newlines to <br> tags
  text = text.replace(/\n/g, '<br>');

  // Step 3: Convert multiple spaces to non-breaking spaces (optional)
  text = text.replace(/ {2}/g, '&nbsp;&nbsp;');

  return text;
});

// Methods
const handleHeaderMediaUpload = async event => {
  const file = event?.file || null;
  template.value.headerMediaFile = file;
  template.value.headerMediaHandle = '';
  if (!file) return;
  try {
    const handle = await store.dispatch('inboxes/metaUpload', {
      inboxId: route.params.inboxId,
      file: file.file,
    });
    template.value.headerMediaHandle = handle || '';
  } catch {
    template.value.headerMediaHandle = '';
  }
};
const handleHeaderMediaRemove = () => {
  template.value.headerMediaFile = null;
  template.value.headerMediaHandle = '';
};

const getFileIcon = file => {
  if (!file?.type) return '📎';
  if (file.type.includes('image')) return '🖼️';
  if (file.type.includes('video')) return '🎥';
  if (file.type.includes('pdf') || file.type.includes('document')) return '📄';

  return '📎';
};
const formatFileSize = bytes => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / k ** i) * 100) / 100 + ' ' + sizes[i];
};
const handleHeaderChange = value => {
  template.value.header = value;
  template.value.headerText = '';
  template.value.headerMediaUrl = '';
  template.value.headerMediaFile = null;
  template.value.headerMediaHandle = '';
};

// Auto-format template name
const handleTemplateNameInput = event => {
  let value = event.target?.value || event;
  // Convert to lowercase
  value = value.toLowerCase();
  // Replace spaces with underscores
  value = value.replace(/\s+/g, '_');
  // Remove invalid characters (keep only a-z, 0-9, _)
  value = value.replace(/[^a-z0-9_]/g, '');
  // Remove consecutive underscores
  value = value.replace(/_+/g, '_');
  // Remove leading/trailing underscores
  value = value.replace(/^_+|_+$/g, '');

  template.value.name = value;
};
const addHeaderVariable = () => {
  if (headerVariables.value.length >= VALIDATION_CONSTANTS.MAX_HEADER_VARIABLES)
    return;
  const nextVar = headerVariables.value.length + 1;
  template.value.headerText += `{{${nextVar}}}`;
};

const addBodyVariable = () => {
  template.value.body += `{{${bodyVariables.value.length + 1}}}`;
};

// Validation Rules
const rules = computed(() => ({
  template: {
    name: {
      required: helpers.withMessage(
        'WHATSAPP_TEMPLATES.ADD.ERRORS.VALUE_REQUIRED_ERROR',
        required
      ),
      maxLength: helpers.withMessage(
        'WHATSAPP_TEMPLATES.ADD.ERRORS.MAX_LENGTH_ERROR',
        maxLength(VALIDATION_CONSTANTS.MAX_TEMPLATE_NAME_LENGTH)
      ),
      templateNameValidator,
    },
    category: {
      required: helpers.withMessage(
        'WHATSAPP_TEMPLATES.ADD.ERRORS.VALUE_REQUIRED_ERROR',
        required
      ),
    },
    language: {
      required: helpers.withMessage(
        'WHATSAPP_TEMPLATES.ADD.ERRORS.VALUE_REQUIRED_ERROR',
        required
      ),
    },
    headerMediaFile: {
      required: requiredIf(() =>
        ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(template.value.header)
      ),
      isValidHeaderFile,
    },
    headerText: {
      required: helpers.withMessage(
        'WHATSAPP_TEMPLATES.ADD.ERRORS.VALUE_REQUIRED_ERROR',
        requiredIf(() => template.value.header === 'TEXT')
      ),
      maxLength: helpers.withMessage(
        'WHATSAPP_TEMPLATES.ADD.ERRORS.MAX_LENGTH_ERROR',
        maxLength(VALIDATION_CONSTANTS.MAX_HEADER_TEXT)
      ),
      onlyOneVariable,
    },
    body: {
      required: helpers.withMessage(
        'WHATSAPP_TEMPLATES.ADD.ERRORS.VALUE_REQUIRED_ERROR',
        required
      ),
      maxLength: helpers.withMessage(
        'WHATSAPP_TEMPLATES.ADD.ERRORS.MAX_LENGTH_ERROR',
        maxLength(VALIDATION_CONSTANTS.MAX_BODY_TEXT)
      ),
      sequentialVariables,
      noAdjacentVariables,
      noStartEndVariable,
    },
    footer: {
      maxLength: helpers.withMessage(
        'WHATSAPP_TEMPLATES.ADD.ERRORS.MAX_LENGTH_ERROR',
        maxLength(VALIDATION_CONSTANTS.MAX_FOOTER_TEXT)
      ),
      maxAllowedVariables: helpers.withMessage(
        'WHATSAPP_TEMPLATES.ADD.ERRORS.NOT_ALLOWED_VARIABLES_ERROR',
        value => extractVariables(value).length === 0
      ),
    },
    cards: {
      validateCarouselCards: helpers.withMessage(
        'WHATSAPP_TEMPLATES.ADD.ERRORS.CAROUSEL_CARDS_ERROR',
        value => {
          if (template.value.format !== 'CAROUSEL') return true;
          return validateCarouselCards.$validator(value);
        }
      ),
    },
    expirationTimeMinutes: {
      required: helpers.withMessage(
        'WHATSAPP_TEMPLATES.ADD.ERRORS.VALUE_REQUIRED_ERROR',
        requiredIf(() => template.value.addExpirationTime)
      ),
      minValue: helpers.withMessage(
        'Expiration time must be at least 1 minute',
        value => {
          if (!template.value.addExpirationTime) return true;
          return value && parseInt(value, 10) >= 1;
        }
      ),
    },
    packageName: {
      required: helpers.withMessage(
        'WHATSAPP_TEMPLATES.ADD.ERRORS.VALUE_REQUIRED_ERROR',
        requiredIf(
          () =>
            isAuthTemplate.value &&
            ['ONE_TAP', 'ZERO_TAP'].includes(
              template.value.authCodeDeliveryMethod
            )
        )
      ),
    },
    signatureHash: {
      required: helpers.withMessage(
        'WHATSAPP_TEMPLATES.ADD.ERRORS.VALUE_REQUIRED_ERROR',
        requiredIf(
          () =>
            isAuthTemplate.value &&
            ['ONE_TAP', 'ZERO_TAP'].includes(
              template.value.authCodeDeliveryMethod
            )
        )
      ),
      length: helpers.withMessage(
        'WHATSAPP_TEMPLATES.AUTHENTICATION.SIGNATURE_HASH_HINT',
        value => {
          if (
            !isAuthTemplate.value ||
            !['ONE_TAP', 'ZERO_TAP'].includes(
              template.value.authCodeDeliveryMethod
            )
          )
            return true;
          return value && value.length === 11;
        }
      ),
    },
  },
  sampleValues: {
    bodyValues: {
      requiredIfKeysPresent: requiredIf(
        () => bodyVariables.value && bodyVariables.value.length > 0
      ),
      allKeysRequired,
    },
    headerValues: {
      requiredIfKeysPresent: requiredIf(
        () => headerVariables.value && headerVariables.value.length > 0
      ),
      allKeysRequired,
    },
  },
}));

const v$ = useVuelidate(rules, { template, sampleValues });

// ADD SUBMIT METHOD
const handleSubmit = async () => {
  v$.value.$touch();
  let isButtonsInvalid = false;
  let isCarouselInvalid = false;
  if (!isAuthTemplate.value) {
    if (template.value.format === 'STANDARD') {
      isButtonsInvalid = await templateButtonsEditor.value.validateButtons();
    } else if (template.value.format === 'CAROUSEL') {
      isCarouselInvalid = await carouselCardEditor.value.validateCards();
    }
  }
  if (v$.value.$invalid || isButtonsInvalid || isCarouselInvalid) return;

  const payload = whatsappTemplatePayload(
    template.value,
    headerVariables.value,
    bodyVariables.value,
    sampleValues.value
  );

  try {
    isCreating.value = true;
    await store.dispatch('inboxes/createWhatsappTemplate', {
      inboxId: route.params.inboxId,
      payload,
    });
    useAlert(t('WHATSAPP_TEMPLATES.ADD.API.SUCCESS_MESSAGE'));
    router.push({
      name: 'settings_inbox_show',
      params: {
        inboxId: route.params.inboxId,
      },
    });
  } catch (error) {
    let errorText =
      error.message ||
      error.error_user_msg ||
      t('WHATSAPP_TEMPLATES.ADD.API.ERROR_MESSAGE');

    useAlert(errorText);
  } finally {
    isCreating.value = false;
  }
};

function reset() {
  template.value = { ...defaultTemplate };
  bodyVariables.value = [];
  headerVariables.value = [];
  sampleValues.value = {
    bodyValues: {},
    headerValues: {},
  };
  v$.value.$reset();
}

// ===== LIFECYCLE HOOKS =====
onDeactivated(() => {
  reset();
});
// ===== WATCH =====
// watch bodyVariables and headerVariables to update sampleValues
watch([bodyVariables, headerVariables], () => {
  sampleValues.value.bodyValues = bodyVariables.value.reduce(
    (acc, variable) => {
      acc[variable] = sampleValues.value.bodyValues[variable] || '';
      return acc;
    },
    {}
  );
  sampleValues.value.headerValues = headerVariables.value.reduce(
    (acc, variable) => {
      acc[variable] = sampleValues.value.headerValues[variable] || '';
      return acc;
    },
    {}
  );
});

// Watch category to enforce constraints
watch(
  () => template.value.category,
  newCategory => {
    template.value.body = '';
    template.value.footer = '';
    template.value.buttons = [];

    if (newCategory !== 'MARKETING') {
      template.value.format = 'STANDARD';
    }
    if (newCategory === 'AUTHENTICATION') {
      handleHeaderChange('NONE');
      // Set initial auth body text
      template.value.body = authText.value.body;
      // Initialize auth specific defaults
      template.value.authCodeDeliveryMethod = 'COPY_CODE';
      template.value.addSecurityRecommendation = false;
      template.value.addExpirationTime = false;
      template.value.expirationTimeMinutes = null;
    }
  }
);

// Watch authentication template options to update body text
watch(
  [
    () => template.value.language,
    () => template.value.addSecurityRecommendation,
    () => template.value.addExpirationTime,
    () => template.value.expirationTimeMinutes,
  ],
  () => {
    if (isAuthTemplate.value) {
      template.value.body = authText.value.body;
      template.value.footer = authText.value.footer;
    }
  }
);

// Watch authentication template options to update FOOTER text
watch(
  () => template.value.expirationTimeMinutes,

  () => {
    if (isAuthTemplate.value) {
      template.value.footer = authText.value.footer;
    }
  }
);

// Watch authCodeDeliveryMethod to update buttons automatically
watch(
  () => template.value.authCodeDeliveryMethod,
  newMethod => {
    if (!isAuthTemplate.value) return;

    if (newMethod === 'ZERO_TAP') {
      template.value.buttons = [];
    } else if (newMethod === 'ONE_TAP') {
      template.value.buttons = [
        {
          type: 'URL',
          text: 'Autofill',
          url: template.value.headerMediaUrl || '',
          hasVariable: false,
        },
      ];
    } else if (newMethod === 'COPY_CODE') {
      template.value.buttons = [
        {
          type: 'COPY_CODE',
          text: 'Copy code',
          example: sampleValues.value.buttonsValues.COPY_CODE || '',
        },
      ];
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="p-4 grid gap-4 grid-cols-3 h-full">
    <!-- Builder -->
    <div class="col-span-2 flex flex-col gap-4">
      <!-- Basic Information -->
      <CollapsibleSection
        :title="$t('WHATSAPP_TEMPLATES.ADD.FORM.BASIC_INFO')"
        always-open
      >
        <div class="flex flex-col gap-4 pb-2">
          <!-- Name -->
          <Input
            v-model="template.name"
            :label="$t('WHATSAPP_TEMPLATES.ADD.FORM.NAME.LABEL')"
            :placeholder="$t('WHATSAPP_TEMPLATES.ADD.FORM.NAME.PLACEHOLDER')"
            :message="
              v$.template.name.$errors[0]
                ? $t(
                    v$.template.name.$errors[0].$message,
                    v$.template.name.$errors[0].$params
                  )
                : ''
            "
            :message-type="v$.template.name.$error ? 'error' : 'info'"
            show-character-count
            :max-length="VALIDATION_CONSTANTS.MAX_TEMPLATE_NAME_LENGTH"
            @input="handleTemplateNameInput"
          >
            <template #hint>
              <p class="text-xs text-n-slate-10 mt-1">
                {{
                  $t('WHATSAPP_TEMPLATES.ADD.ERRORS.TEMPLATE_NAME_FORMAT_ERROR')
                }}
              </p>
            </template>
          </Input>
          <div class="grid grid-cols-2 gap-4">
            <!-- Category -->
            <div class="col-span-1">
              <label>
                {{ $t('WHATSAPP_TEMPLATES.ADD.FORM.CATEGORY.LABEL') }}
              </label>
              <select v-model="template.category">
                <option
                  v-for="option in WHATSAPP_CATEGORY_OPTIONS"
                  :key="option.id"
                  :value="option.id"
                >
                  {{ option.name }}
                </option>
              </select>
              <p
                v-if="v$.template.category.$error"
                class="mt-1 text-n-ruby-9 dark:text-n-ruby-9 text-xs"
              >
                {{
                  v$.template.category.$errors[0]
                    ? $t(
                        v$.template.category.$errors[0].$message,
                        v$.template.category.$errors[0].$params
                      )
                    : ''
                }}
              </p>
            </div>
            <!-- Language -->
            <div class="col-span-1">
              <label>
                {{ $t('WHATSAPP_TEMPLATES.ADD.FORM.LANGUAGE.LABEL') }}
              </label>
              <select v-model="template.language">
                <option
                  v-for="option in LANGUAGE_OPTIONS"
                  :key="option.id"
                  :value="option.id"
                >
                  {{ option.name }}
                </option>
              </select>
              <p
                v-if="v$.template.language.$error"
                class="mt-1 text-n-ruby-9 dark:text-n-ruby-9 text-xs"
              >
                {{
                  v$.template.language.$errors[0]
                    ? $t(
                        v$.template.language.$errors[0].$message,
                        v$.template.language.$errors[0].$params
                      )
                    : ''
                }}
              </p>
            </div>
          </div>
          <!-- Format Selector if template MARKETING -->
          <div v-if="template.category === 'MARKETING'">
            <label>{{ $t('WHATSAPP_TEMPLATES.ADD.FORM.FORMAT.LABEL') }}</label>
            <div class="flex gap-4 mt-2">
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  v-model="template.format"
                  type="radio"
                  value="STANDARD"
                  class="radio-input"
                />
                <span class="text-sm">
                  {{ $t('WHATSAPP_TEMPLATES.ADD.FORM.FORMAT.STANDARD') }}
                </span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  v-model="template.format"
                  type="radio"
                  value="CAROUSEL"
                  class="radio-input"
                />
                <span class="text-sm">
                  {{ $t('WHATSAPP_TEMPLATES.ADD.FORM.FORMAT.CAROUSEL') }}
                </span>
              </label>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      <!-- Authentication Code Delivery Setup -->
      <CollapsibleSection
        v-if="isAuthTemplate"
        :title="$t('WHATSAPP_TEMPLATES.AUTHENTICATION.CODE_DELIVERY_TITLE')"
        :subtitle="
          $t('WHATSAPP_TEMPLATES.AUTHENTICATION.CODE_DELIVERY_SUBTITLE')
        "
        always-open
      >
        <div class="flex flex-col gap-3">
          <!-- Zero-tap autofill option -->
          <div class="flex items-start gap-2 text-n-slate-10 font-medium">
            <input
              id="deliveryMethodZeroTap"
              v-model="template.authCodeDeliveryMethod"
              type="radio"
              value="ZERO_TAP"
              class="mt-1"
            />
            <div class="flex flex-col gap-1">
              <label for="deliveryMethodZeroTap" class="text-sm cursor-pointer">
                {{
                  $t(
                    'WHATSAPP_TEMPLATES.AUTHENTICATION.DELIVERY_METHOD_ZERO_TAP'
                  )
                }}
              </label>
            </div>
          </div>

          <!-- Copy code option -->
          <div class="flex items-start gap-2">
            <input
              id="deliveryMethodCopyCode"
              v-model="template.authCodeDeliveryMethod"
              type="radio"
              value="COPY_CODE"
              class="mt-1"
            />
            <div class="flex flex-col gap-1 w-full">
              <label
                for="deliveryMethodCopyCode"
                class="text-sm font-medium cursor-pointer"
              >
                {{
                  $t(
                    'WHATSAPP_TEMPLATES.AUTHENTICATION.DELIVERY_METHOD_COPY_CODE'
                  )
                }}
              </label>
              <div
                v-if="template.authCodeDeliveryMethod === 'COPY_CODE'"
                class="mt-2 pl-2 border-l-2 border-n-brand"
              >
                <Input
                  v-model="sampleValues.buttonsValues['COPY_CODE']"
                  :label="
                    $t('WHATSAPP_TEMPLATES.AUTHENTICATION.EXAMPLE_CODE_LABEL')
                  "
                  placeholder="e.g., 123456"
                  message-type="info"
                />
              </div>
            </div>
          </div>

          <!-- One-tap autofill option -->
          <div class="flex items-start gap-2">
            <input
              id="deliveryMethodOneTap"
              v-model="template.authCodeDeliveryMethod"
              type="radio"
              value="ONE_TAP"
              class="mt-1"
            />
            <div class="flex flex-col gap-1">
              <label for="deliveryMethodOneTap" class="text-sm cursor-pointer">
                {{
                  $t(
                    'WHATSAPP_TEMPLATES.AUTHENTICATION.DELIVERY_METHOD_ONE_TAP'
                  )
                }}
              </label>
            </div>
          </div>

          <div
            v-if="
              ['ONE_TAP', 'ZERO_TAP'].includes(template.authCodeDeliveryMethod)
            "
            class="mt-2 pl-2 border-l-2 border-n-brand flex flex-col gap-3"
          >
            <Input
              v-model="template.packageName"
              :label="
                $t('WHATSAPP_TEMPLATES.AUTHENTICATION.PACKAGE_NAME_LABEL')
              "
              :placeholder="
                $t('WHATSAPP_TEMPLATES.AUTHENTICATION.PACKAGE_NAME_PLACEHOLDER')
              "
              :message="
                v$.template.packageName.$errors[0]
                  ? $t(
                      v$.template.packageName.$errors[0].$message,
                      v$.template.packageName.$errors[0].$params
                    )
                  : ''
              "
              :message-type="v$.template.packageName.$error ? 'error' : ''"
            />
            <Input
              v-model="template.signatureHash"
              :label="
                $t('WHATSAPP_TEMPLATES.AUTHENTICATION.SIGNATURE_HASH_LABEL')
              "
              :placeholder="
                $t(
                  'WHATSAPP_TEMPLATES.AUTHENTICATION.SIGNATURE_HASH_PLACEHOLDER'
                )
              "
              :message="
                v$.template.signatureHash.$errors[0]
                  ? $t(
                      v$.template.signatureHash.$errors[0].$message,
                      v$.template.signatureHash.$errors[0].$params
                    )
                  : $t('WHATSAPP_TEMPLATES.AUTHENTICATION.SIGNATURE_HASH_HINT')
              "
              :message-type="
                v$.template.signatureHash.$error ? 'error' : 'info'
              "
            />
          </div>
        </div>
      </CollapsibleSection>

      <!-- Header (Standard Only, Not for Authentication) -->
      <CollapsibleSection
        v-if="
          template.format === 'STANDARD' &&
          template.category !== 'AUTHENTICATION'
        "
        :title="$t('WHATSAPP_TEMPLATES.ADD.FORM.HEADER.LABEL')"
        :subtitle="$t('WHATSAPP_TEMPLATES.ADD.FORM.OPTIONAL')"
        :initial-open="false"
      >
        <div class="flex flex-col gap-2 pb-2">
          <div class="flex items-center justify-between">
            <div class="flex flex-col gap-1">
              <p class="text-xs text-gray-500">
                {{ $t('WHATSAPP_TEMPLATES.ADD.FORM.HEADER_DESCRIPTION') }}
              </p>
            </div>
          </div>
          <div class="flex gap-4 items-center">
            <div
              v-for="item in HEADER_OPTIONS"
              :key="item.id"
              class="flex gap-4 items-center"
            >
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  v-model="template.header"
                  class="radio-input"
                  name="header"
                  type="radio"
                  :value="item.id"
                  @change="handleHeaderChange(item.id)"
                />
                <span class="text-sm">{{ item.title }}</span>
              </label>
            </div>
          </div>
          <!-- Header Text Input (if TEXT selected) -->
          <div v-if="template.header === 'TEXT'" class="flex flex-col gap-2">
            <Input
              v-model="template.headerText"
              :label="$t('WHATSAPP_TEMPLATES.ADD.FORM.HEADER_TEXT_LABEL')"
              :placeholder="
                $t('WHATSAPP_TEMPLATES.ADD.FORM.HEADER_TEXT_PLACEHOLDER')
              "
              :message="
                v$.template.headerText.$errors[0]
                  ? $t(
                      v$.template.headerText.$errors[0].$message,
                      v$.template.headerText.$errors[0].$params
                    )
                  : ''
              "
              :message-type="v$.template.headerText.$error ? 'error' : 'info'"
              show-character-count
              :max-length="VALIDATION_CONSTANTS.MAX_HEADER_TEXT"
            />
            <Button
              :disabled="
                headerVariables.length ===
                VALIDATION_CONSTANTS.MAX_HEADER_VARIABLES
              "
              :label="$t('WHATSAPP_TEMPLATES.ADD.FORM.ADD_HEADER_VARIABLE')"
              color="blue"
              variant="faded"
              size="md"
              @click="addHeaderVariable"
            />
            <!-- Sample value for header variable -->
            <div
              v-if="headerVariables.length > 0"
              class="p-4 border border-n-brand dark:border-n-brand rounded-lg"
            >
              <p class="text-sm font-medium mb-3">
                {{ $t('WHATSAPP_TEMPLATES.ADD.FORM.SAMPLE_VALUES_HEADER') }}
              </p>
              <div
                v-for="variable in headerVariables"
                :key="variable"
                class="mb-2"
              >
                <Input
                  v-model="sampleValues.headerValues[variable]"
                  :label="
                    $t('WHATSAPP_TEMPLATES.ADD.FORM.VARIABLE_EXAMPLE_LABEL', {
                      variable,
                    })
                  "
                  :placeholder="
                    $t(
                      'WHATSAPP_TEMPLATES.ADD.FORM.VARIABLE_EXAMPLE_PLACEHOLDER'
                    )
                  "
                />
              </div>
              <p
                v-if="v$.$dirty && v$.sampleValues.headerValues.$invalid"
                class="text-red-800 dark:text-red-800"
              >
                {{
                  $t('WHATSAPP_TEMPLATES.ADD.FORM.VARIABLES_VALIDATION_ERROR')
                }}
              </p>
            </div>
          </div>

          <!-- Header Media URL (if IMAGE/VIDEO/DOCUMENT selected) -->
          <div
            v-if="['IMAGE', 'VIDEO', 'DOCUMENT'].includes(template.header)"
            class="border border-n-weak rounded-lg p-4"
          >
            <!-- Upload -->
            <FileUpload
              input-id="headerAttachment"
              :size="4096 * 4096"
              :accept="MEDIA_CONFIG[template.header].accept"
              drop
              :drop-directory="false"
              @input-file="handleHeaderMediaUpload"
            >
              <div
                class="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors cursor-pointer w-full text-center"
              >
                <woot-button
                  class-names="button--upload mb-2"
                  icon="attach"
                  emoji="📎"
                  color-scheme="secondary"
                  variant="smooth"
                  size="small"
                >
                  {{ $t('WHATSAPP_TEMPLATES.ADD.FORM.CHOOSE_FILE') }}
                </woot-button>
                <span class="text-xs text-gray-500">
                  {{ $t('WHATSAPP_TEMPLATES.ADD.FORM.DRAG_DROP_TEXT') }}
                </span>
              </div>
            </FileUpload>
            <!-- Preview -->
            <div
              v-if="template.headerMediaFile"
              class="flex items-center p-1 bg-slate-50 dark:bg-slate-800 gap-1 rounded-md w-[15rem] mb-1 mt-2"
            >
              <div class="max-w-[4rem] flex-shrink-0 w-6 flex items-center">
                {{ getFileIcon(template.headerMediaFile) }}
              </div>
              <div class="max-w-3/5 min-w-[50%] overflow-hidden text-ellipsis">
                <span
                  class="h-4 overflow-hidden text-sm font-medium text-ellipsis whitespace-nowrap"
                >
                  {{ template.headerMediaFile.name }}
                </span>
              </div>
              <div class="w-[30%] justify-center">
                <span
                  class="overflow-hidden text-xs text-ellipsis whitespace-nowrap"
                >
                  {{ formatFileSize(template.headerMediaFile.size) }}
                </span>
              </div>
              <div class="flex items-center justify-center">
                <woot-button
                  class="!w-6 !h-6 text-sm rounded-md hover:bg-slate-50 dark:hover:bg-slate-800 clear secondary"
                  icon="dismiss"
                  @click="handleHeaderMediaRemove"
                />
              </div>
            </div>
          </div>
          <p
            v-if="v$.$dirty && v$.template.headerMediaFile.$invalid"
            class="text-red-800 dark:text-red-800"
          >
            {{
              v$.template.headerMediaFile.$errors[0]
                ? $t(
                    v$.template.headerMediaFile.$errors[0].$message,
                    v$.template.headerMediaFile.$errors[0].$params
                  )
                : ''
            }}
          </p>
        </div>
      </CollapsibleSection>

      <!-- Body -->
      <CollapsibleSection
        :title="$t('WHATSAPP_TEMPLATES.ADD.FORM.BODY.LABEL')"
        always-open
      >
        <div class="flex flex-col gap-2 pb-2">
          <!-- Authentication Template Body -->
          <div v-if="isAuthTemplate">
            <p class="text-xs text-n-slate-10 mb-3">
              {{ $t('WHATSAPP_TEMPLATES.AUTHENTICATION.BODY_INTRO') }}
            </p>

            <!-- Add Security Recommendation -->
            <div class="flex items-start gap-2 mb-3">
              <input
                id="addSecurityRecommendation"
                v-model="template.addSecurityRecommendation"
                type="checkbox"
                class="mt-1"
              />
              <label
                for="addSecurityRecommendation"
                class="text-sm cursor-pointer"
              >
                {{
                  $t(
                    'WHATSAPP_TEMPLATES.AUTHENTICATION.SECURITY_RECOMMENDATION_LABEL'
                  )
                }}
              </label>
            </div>

            <!-- Sample value for OTP code -->
            <div
              class="p-4 border border-n-brand dark:border-n-brand rounded-lg"
            >
              <p class="text-sm font-medium mb-3">
                {{ $t('WHATSAPP_TEMPLATES.AUTHENTICATION.OTP_SAMPLE_LABEL') }}
              </p>
              <Input
                v-model="sampleValues.bodyValues[1]"
                :label="
                  $t('WHATSAPP_TEMPLATES.AUTHENTICATION.OTP_VARIABLE_LABEL')
                "
                placeholder="e.g., 123456"
              />
            </div>
          </div>

          <!-- Standard Template Body -->
          <template v-else>
            <TextArea
              v-model="template.body"
              label-class="sr-only"
              :placeholder="$t('WHATSAPP_TEMPLATES.ADD.FORM.BODY_PLACEHOLDER')"
              show-character-count
              :max-length="VALIDATION_CONSTANTS.MAX_BODY_TEXT"
              :message="
                v$.template.body.$errors[0]
                  ? $t(
                      v$.template.body.$errors[0].$message,
                      v$.template.body.$errors[0].$params
                    )
                  : ''
              "
              :message-type="v$.template.body.$error ? 'error' : 'info'"
              rows="5"
            />
            <div class="text-end">
              <Button
                :label="$t('WHATSAPP_TEMPLATES.ADD.FORM.ADD_VARIABLE')"
                color="blue"
                variant="faded"
                size="md"
                @click="addBodyVariable"
              />
            </div>

            <!-- Sample values for body variables -->
            <div
              v-if="bodyVariables.length > 0"
              class="p-4 border border-n-brand dark:border-n-brand rounded-lg"
            >
              <p class="text-sm font-medium mb-3">
                {{ $t('WHATSAPP_TEMPLATES.ADD.FORM.SAMPLE_VALUES_BODY') }}
              </p>
              <div
                v-for="variable in bodyVariables"
                :key="variable"
                class="mb-2"
              >
                <Input
                  v-model="sampleValues.bodyValues[variable]"
                  :label="
                    $t('WHATSAPP_TEMPLATES.ADD.FORM.VARIABLE_EXAMPLE_LABEL', {
                      variable,
                    })
                  "
                  :placeholder="
                    $t(
                      'WHATSAPP_TEMPLATES.ADD.FORM.VARIABLE_EXAMPLE_PLACEHOLDER'
                    )
                  "
                />
              </div>
              <p
                v-if="v$.$dirty && v$.sampleValues.bodyValues.$invalid"
                class="text-red-800 dark:text-red-800"
              >
                {{
                  $t('WHATSAPP_TEMPLATES.ADD.FORM.VARIABLES_VALIDATION_ERROR')
                }}
                {{
                  v$.sampleValues.bodyValues.$errors[0]
                    ? $t(
                        v$.sampleValues.bodyValues.$errors[0].$message,
                        v$.sampleValues.bodyValues.$errors[0].$params
                      )
                    : ''
                }}
              </p>
            </div>
          </template>
        </div>
      </CollapsibleSection>

      <!-- Footer (Standard Only) -->
      <CollapsibleSection
        v-if="!isAuthTemplate && template.format === 'STANDARD'"
        :title="$t('WHATSAPP_TEMPLATES.ADD.FORM.FOOTER.LABEL')"
        :subtitle="$t('WHATSAPP_TEMPLATES.ADD.FORM.OPTIONAL')"
        :initial-open="false"
      >
        <div class="pb-2">
          <Input
            v-model="template.footer"
            label=""
            :placeholder="$t('WHATSAPP_TEMPLATES.ADD.FORM.FOOTER_PLACEHOLDER')"
            :message="
              v$.template.footer.$errors[0]
                ? $t(
                    v$.template.footer.$errors[0].$message,
                    v$.template.footer.$errors[0].$params
                  )
                : ''
            "
            :message-type="v$.template.footer.$error ? 'error' : 'info'"
            show-character-count
            :max-length="VALIDATION_CONSTANTS.MAX_FOOTER_TEXT"
          >
            <template #hint>
              <p class="text-xs text-n-slate-10 mt-1">
                {{ $t('WHATSAPP_TEMPLATES.ADD.FORM.FOOTER_HINT') }}
              </p>
            </template>
          </Input>
        </div>
      </CollapsibleSection>

      <!-- Authentication Footer (Expiration) -->
      <CollapsibleSection
        v-if="isAuthTemplate"
        :title="$t('WHATSAPP_TEMPLATES.ADD.FORM.FOOTER.LABEL')"
        always-open
      >
        <div class="flex flex-col gap-2 pb-2">
          <!-- Add Expiration Time -->
          <div class="flex flex-col gap-2 mb-3">
            <div class="flex items-start gap-2">
              <input
                id="addExpirationTime"
                v-model="template.addExpirationTime"
                type="checkbox"
                class="mt-1"
              />
              <label
                for="addExpirationTime"
                class="text-sm cursor-pointer font-medium"
              >
                {{
                  $t('WHATSAPP_TEMPLATES.AUTHENTICATION.EXPIRATION_TIME_LABEL')
                }}
              </label>
            </div>
            <div
              v-if="template.addExpirationTime"
              class="ml-6 flex items-center gap-4 pl-2 border-l-2 border-n-brand"
            >
              <div class="w-32">
                <Input
                  v-model="template.expirationTimeMinutes"
                  type="number"
                  :label="
                    $t(
                      'WHATSAPP_TEMPLATES.AUTHENTICATION.EXPIRATION_MINUTES_LABEL'
                    )
                  "
                  placeholder="e.g., 10"
                  :message="
                    v$.template.expirationTimeMinutes.$errors[0]
                      ? $t(
                          v$.template.expirationTimeMinutes.$errors[0].$message,
                          v$.template.expirationTimeMinutes.$errors[0].$params
                        )
                      : ''
                  "
                  :message-type="
                    v$.template.expirationTimeMinutes.$error ? 'error' : 'info'
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      <!-- Buttons (Standard Only) -->
      <CollapsibleSection
        v-if="!isAuthTemplate && template.format === 'STANDARD'"
        :title="`Buttons (${template.buttons.length}/${VALIDATION_CONSTANTS.MAX_TOTAL_BUTTONS})`"
        :subtitle="$t('WHATSAPP_TEMPLATES.ADD.FORM.BUTTONS_SUBTITLE')"
        :initial-open="false"
      >
        <TemplateButtonsEditor
          ref="templateButtonsEditor"
          v-model="template.buttons"
          :category="template.category"
          :language="template.language"
        />
      </CollapsibleSection>

      <!-- Carousel Editor -->
      <CollapsibleSection
        v-if="template.format === 'CAROUSEL'"
        :title="`Carousel Cards (Min ${VALIDATION_CONSTANTS.MIN_CAROUSEL_CARDS}, Max ${VALIDATION_CONSTANTS.MAX_CAROUSEL_CARDS})`"
        :subtitle="$t('WHATSAPP_TEMPLATES.ADD.FORM.CAROUSEL_SUBTITLE')"
      >
        <CarouselCardEditor ref="carouselCardEditor" v-model="template.cards" />
      </CollapsibleSection>

      <!-- Submit Button -->
      <div class="pb-4">
        <Button
          color="blue"
          class="w-full"
          :label="$t('WHATSAPP_TEMPLATES.ADD.SUBMIT')"
          :is-loading="isCreating"
          @click="handleSubmit"
        />
      </div>
    </div>

    <!-- Preview Section -->
    <div class="sticky top-0 self-start">
      <WhatsappTemplatePreview
        :template="template"
        :preview-header="previewHeader"
        :preview-body-with-highlight="previewBodyWithHighlight"
      />
    </div>
  </div>
</template>
